import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';
import Swal from 'sweetalert2';
import { Icon } from '@iconify/react';
import InvoiceDetailModal from './InvoiceDetailModal';
import PatientInvoiceDetailModal from './PatientInvoiceDetailModal';

const BillingManager = () => {
    // Data State
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('subscription'); // 'subscription' or 'clinic'

    // Filter State
    const [searchQuery, setSearchQuery] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Detail Modal State
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    // Fetch on Tab Change
    useEffect(() => {
        setInvoices([]); // Clear previous data immediately
        fetchInvoices();
        // Reset filters on tab swich
        setSearchQuery('');
        setStartDate('');
        setEndDate('');
    }, [activeTab]);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            // Select Endpoint based on Tab
            const endpoint = activeTab === 'subscription'
                ? '/clinic/subscription-invoices'
                : '/clinic/patient-invoices';

            const response = await api.get(endpoint);
            console.log(`[BillingManager] Fetched ${activeTab} invoices:`, response.data);
            setInvoices(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching invoices:', error);
            setLoading(false);
        }
    };

    // Client-Side Search/Date Filter (Backend Segregation is already done)
    const getFilteredInvoices = () => {
        return invoices.filter(inv => {
            // Search Logic
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch =
                (inv.invoiceNumber && inv.invoiceNumber.toLowerCase().includes(searchLower)) ||
                (inv.patientId?.name && inv.patientId.name.toLowerCase().includes(searchLower)) ||
                (inv._id && inv._id.toLowerCase().includes(searchLower));

            // Date Logic
            let matchesDate = true;
            if (startDate || endDate) {
                const invDate = new Date(inv.date || inv.createdAt || inv.paidAt).setHours(0, 0, 0, 0);
                if (startDate && invDate < new Date(startDate).setHours(0, 0, 0, 0)) matchesDate = false;
                if (endDate && invDate > new Date(endDate).setHours(0, 0, 0, 0)) matchesDate = false;
            }

            return matchesSearch && matchesDate;
        });
    };

    // Email Handler
    const handleEmailInvoice = async (invoiceId) => {
        const invoice = invoices.find(inv => inv._id === invoiceId);
        if (!invoice) return;

        Swal.fire({
            title: 'Email Invoice',
            input: 'email',
            inputLabel: 'Send invoice to:',
            inputValue: activeTab === 'subscription' ? (invoice.clinicId?.email) : (invoice.patientId?.email),
            inputAttributes: {
                autocapitalize: 'off',
                style: 'color: #000 !important; font-weight: 500; background-color: #fff; opacity: 1;'
            },
            customClass: {
                input: 'text-dark fw-medium'
            },
            showCancelButton: true,
            confirmButtonText: 'Send Email',
            showLoaderOnConfirm: true,
            preConfirm: async (email) => {
                if (!email) Swal.showValidationMessage('Please enter an email address');
                try {
                    await api.post(`/clinic/invoices/${invoiceId}/email`, { targetEmail: email });
                    return true;
                } catch (error) {
                    Swal.showValidationMessage(error.response?.data?.message || 'Failed to send email');
                }
            }
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire('Sent!', 'Invoice has been emailed.', 'success');
            }
        });
    };

    const handleDownloadInvoice = (invoice) => {
        setSelectedInvoice(invoice);
        setShowDetailModal(true);
    };

    const getStatusColor = (status) => {
        return status === 'Paid' ? 'bg-success-focus text-success-600' : 'bg-warning-focus text-warning-600';
    };

    const filteredInvoices = getFilteredInvoices();

    return (
        <div className="card h-100 p-0 radius-12 fade-in-up border-0 shadow-sm">
            {/* Header & Filters */}
            <div className="card-header border-bottom bg-base py-16 px-24">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                    <div className="d-flex align-items-center gap-3">
                        <div className="w-40-px h-40-px radius-8 bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center text-xl shadow-sm">
                            <Icon icon="solar:bill-list-bold" />
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-dark">Billing & Invoices</h5>
                            <p className="text-sm text-secondary mb-0">Manage subscriptions and consultation fees</p>
                        </div>
                    </div>
                </div>

                {/* Tabs & Search Bar Row */}
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex bg-light p-1 rounded-3">
                        <button
                            onClick={() => setActiveTab('subscription')}
                            className={`px-3 py-2 text-sm fw-bold rounded-2 transition-all border-0 ${activeTab === 'subscription' ? 'bg-white shadow-sm text-primary' : 'bg-transparent text-secondary hover-text-primary'}`}
                        >
                            Subscription Plans
                        </button>
                        <button
                            onClick={() => setActiveTab('clinic')}
                            className={`px-3 py-2 text-sm fw-bold rounded-2 transition-all border-0 ${activeTab === 'clinic' ? 'bg-white shadow-sm text-primary' : 'bg-transparent text-secondary hover-text-primary'}`}
                        >
                            Clinic Invoices
                        </button>
                    </div>

                    <div className="d-flex gap-2 flex-grow-1 justify-content-end">
                        <div className="position-relative">
                            <input
                                type="text"
                                className="form-control ps-5 radius-8"
                                placeholder={`Search ${activeTab === 'subscription' ? 'Plan' : 'Patient'} Invoices...`}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ minWidth: '200px' }}
                            />
                            <Icon icon="solar:magnifer-linear" className="position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary" />
                        </div>
                        <input
                            type="date"
                            className="form-control w-auto radius-8 text-secondary text-sm"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                        <input
                            type="date"
                            className="form-control w-auto radius-8 text-secondary text-sm"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                        <button
                            className="btn btn-outline-secondary btn-sm radius-8 px-3"
                            onClick={() => { setSearchQuery(''); setStartDate(''); setEndDate(''); }}
                            title="Clear Filters"
                        >
                            <Icon icon="solar:refresh-linear" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Table Content */}
            <div className="card-body p-0">
                <div className="table-responsive">
                    <table className="table bordered-table mb-0 align-middle table-hover">
                        <thead className="bg-light border-bottom">
                            <tr>
                                <th className="ps-4 py-3 text-secondary text-xs fw-bold text-uppercase">Invoice ID</th>
                                <th className="py-3 text-secondary text-xs fw-bold text-uppercase">{activeTab === 'subscription' ? 'Plan / Period' : 'Patient / Doctor'}</th>
                                <th className="py-3 text-secondary text-xs fw-bold text-uppercase">Date</th>
                                <th className="py-3 text-secondary text-xs fw-bold text-uppercase">Amount</th>
                                <th className="py-3 text-secondary text-xs fw-bold text-uppercase">Status</th>
                                <th className="py-3 text-end pe-4 text-secondary text-xs fw-bold text-uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border text-primary"></div></td></tr>
                            ) : filteredInvoices.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-5 text-secondary">
                                        <div className="d-flex flex-column align-items-center opacity-50">
                                            <Icon icon="solar:bill-check-linear" className="display-4 mb-2" />
                                            <p className="mb-0">No {activeTab} invoices found.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredInvoices.map(inv => (
                                    <tr key={inv._id}>
                                        <td className="ps-4 fw-bold text-primary text-sm">#{inv.invoiceNumber || inv._id.slice(-6).toUpperCase()}</td>
                                        <td>
                                            {activeTab === 'subscription' ? (
                                                <div className="d-flex flex-column">
                                                    <span className="fw-bold text-dark">{inv.subscriptionPlan} Plan</span>
                                                    <span className="text-xs text-secondary">{inv.period || 'Monthly'}</span>
                                                </div>
                                            ) : (
                                                <div className="d-flex flex-column">
                                                    <span className="fw-bold text-dark">{inv.patientId?.name || 'Guest Patient'}</span>
                                                    <span className="text-xs text-secondary">Dr. {inv.doctorId?.name?.replace("Dr.", "")?.replace("Mr.", "")?.replace("Mrs.", "")?.replace("Ms.", "")?.replace("Miss.", "")?.replace("dr.", "") || 'General'}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-secondary text-sm">{new Date(inv.date || inv.createdAt).toLocaleDateString()}</td>
                                        <td className="fw-bold text-dark">{inv.currency} {Number(inv.amount).toLocaleString()}</td>
                                        <td>
                                            <span className={`px-2 py-1 radius-4 text-xs fw-bold ${getStatusColor(inv.status)}`}>
                                                {inv.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="text-end pe-4">
                                            <div className="d-flex justify-content-end gap-2">
                                                <button
                                                    onClick={() => handleDownloadInvoice(inv)}
                                                    className="btn btn-sm btn-light text-primary-600 hover-bg-primary-50 fw-bold d-flex align-items-center gap-1 radius-6 px-2"
                                                    title="View / Download"
                                                >
                                                    <Icon icon="solar:download-bold" />
                                                </button>
                                                <button
                                                    onClick={() => handleEmailInvoice(inv._id)}
                                                    className="btn btn-sm btn-light text-info-600 hover-bg-info-50 fw-bold d-flex align-items-center gap-1 radius-6 px-2"
                                                    title="Email Invoice"
                                                >
                                                    <Icon icon="solar:letter-bold" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* CONDITIONAL MODAL RENDERING */}
            {activeTab === 'subscription' ? (
                // SUPER ADMIN / PLAN STYLE MODAL
                <InvoiceDetailModal
                    show={showDetailModal}
                    onHide={() => setShowDetailModal(false)}
                    invoice={selectedInvoice}
                    onEmail={handleEmailInvoice}
                />
            ) : (
                // CLINIC / PATIENT STYLE MODAL
                <PatientInvoiceDetailModal
                    show={showDetailModal}
                    onHide={() => setShowDetailModal(false)}
                    invoice={selectedInvoice}
                    onEmail={handleEmailInvoice}
                />
            )}

        </div>
    );
};

export default BillingManager;
